import React, { JSX } from 'react';
import {
  Field,
  ImageField,
  Link,
  LinkField,
  Text,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { decodeHTMLEntities } from 'assets/utils/helper';
import { formatProviderUrl } from 'assets/utils/formatUrl';
import { LocationItem } from 'components/types/LocationTypes';
import Icons from 'components/atoms/Icons';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { StyleguideComponentProps } from 'lib/component-props';
import Constants from '../../assets/utils/constant';

const buildContentHubProviderImageUrl = (
  firstName?: string,
  lastName?: string,
  npi?: string
): string => {
  if (!firstName || !lastName || !npi) {
    return '';
  }

  const cleanValue = (value: string): string =>
    value
      .trim()
      .toLowerCase()
      .replace(/\.\s+/g, '.')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9.-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  return `${Constants.api.contentHubProviderImageBaseUrl}/${cleanValue(
    firstName
  )}-${cleanValue(lastName)}-${npi}.jpg`;
};

type ProviderItem = {
  id: string;
  name: string;
  fields: {
    FirstName: {
      value: string;
    };
    url: string;
    FullName: Field<string>;
    Degrees: Field<string>;
    ExternalId: Field<string>;
    Specialty: Field<string>;
    City: {
      value: string;
    };
    LastName: {
      value: string;
    };
    NPI: Field<string>;
    Link: LinkField;
    ProviderImage: ImageField;
    Gender: Field<string>;
    Locations?: Array<LocationItem>;
  };
};

type FeaturedProvidersProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    Title: Field<string>;
    Providers: Array<ProviderItem>;
    link: LinkField;
    BaseViewMoreLink: LinkField;
  };
};

const FeaturedProviders = (props: FeaturedProvidersProps): JSX.Element => {
  if (props == undefined) {
    return <div>Data source is not defined</div>;
  }

  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );
  const { fields } = props;

  if (fields == undefined || fields.Providers == undefined) {
    return <div>Data source is not defined</div>;
  }

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`featured-providers-${props.rendering?.uid}`}
    >
      <div className="jss-inner-container">
        <div className="featured-list providers">
          {fields?.Title?.value && (
            <h2 className="title">{fields?.Title?.value}</h2>
          )}
          <div className="list-inner">
            {fields?.Providers?.map(({ id, fields }, index) => {
              const providerCities = [
                ...new Set(
                  fields?.Locations?.map(
                    loc => loc?.fields?.City?.value
                  ).filter(Boolean)
                ),
              ];

              return (
                <a
                  key={id}
                  href={formatProviderUrl(
                    fields.ExternalId?.value,
                    fields.FirstName?.value,
                    fields.LastName?.value
                  )}
                  className="featured-item bg-secondary"
                >
                  <div className="item-top">
                    <div className="item-image">
                      {(() => {
                        const providerImageUrl = buildContentHubProviderImageUrl(
                          fields.FirstName?.value,
                          fields.LastName?.value,
                          fields.NPI?.value
                        );

                        const fallbackImage = `${Constants.api.contentHubProviderImageBaseUrl}/placeholder-${
                          fields.Gender?.value === 'Male' ? 'male' : 'female'
                        }-provider-transparent`;
                        
                        const html = `<img src="${providerImageUrl || fallbackImage}" alt="${
                          `image-${index}`
                        }" onerror="this.onerror=null;this.src='${fallbackImage}';" />`;

                        return (
                          <div
                            dangerouslySetInnerHTML={{ __html: html }}
                            suppressHydrationWarning
                          />
                        );
                      })()}
                    </div>
                    <div
                      className="content-container "
                      itemScope
                      itemType="http://schema.org/Physician"
                    >
                      {(fields?.FullName?.value || fields?.Degrees?.value) && (
                        <Text
                          className="item-name"
                          itemProp="name"
                          tag="h3"
                          field={{
                            value: `${fields?.FullName.value || ''}, ${fields?.Degrees.value || ''}`,
                          }}
                        />
                      )}
                      <div className="specialties">
                        {fields?.Specialty?.value && (
                        <p
                          className="item-subtitle"
                          itemType="https://schema.org/medicalSpecialty"
                        >
                          {decodeHTMLEntities(fields.Specialty.value)
                            .split('|')
                            .map((s: string) => s.trim())
                            .filter((s: string) => s.length > 0)
                            .join(', ')}
                        </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="locations">
                    {providerCities.length > 0 && (
                      <>
                        <svg className="icon icon-location">
                          <Icons id="icon-location" />
                        </svg>
                        <div className="city-list">
                          {providerCities.join(', ')}
                        </div>
                      </>
                    )}
                  </div>
                </a>
              );
            })}
          </div>
          {fields?.BaseViewMoreLink?.value?.href && (
            <div className="view-more">
              <Link
                className="btn-primary"
                field={props.fields?.BaseViewMoreLink}
              />
              <div className="divider"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedProviders;